import { computed, ref, Ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql } from '@/gql'
import type { OwnerOption } from '@/components/Owners/OwnersPicker.vue'
import { toOwnerOption } from '@/components/Owners/fragments'
import type { OwnerCandidatesQuery, RecruiterOwnerCandidatesQuery } from '@/gql/graphql'

export type OwnerTeamAccount = { type: 'company' | 'recruiter'; id: string }

const COMPANY_CANDIDATES = graphql(`
  query OwnerCandidates($id: ID!, $search: String, $page: Int) {
    company(id: $id) {
      id
      teamMembersAndOwners(search: $search, first: 50, page: $page) {
        data {
          id
          ...OwnerUserFields
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  }
`)

const RECRUITER_CANDIDATES = graphql(`
  query RecruiterOwnerCandidates($id: ID!, $search: String, $page: Int) {
    recruiter(id: $id) {
      id
      teamMembersAndOwners(search: $search, first: 50, page: $page) {
        data {
          id
          ...OwnerUserFields
        }
        paginatorInfo {
          currentPage
          hasMorePages
        }
      }
    }
  }
`)

type CandidatesResult = Partial<OwnerCandidatesQuery & RecruiterOwnerCandidatesQuery>

const rootOf = (result: CandidatesResult | undefined) => result?.company ?? result?.recruiter ?? null

/** Who may be made an owner on a company or agency account: its team members plus the account owner. */
export default function useOwnerCandidates(
  account: () => OwnerTeamAccount | null,
  search: Ref<string>,
) {
  const { result, loading, fetchMore } = useQuery<CandidatesResult>(
    () => (account()?.type === 'recruiter' ? RECRUITER_CANDIDATES : COMPANY_CANDIDATES),
    () => ({ id: account()?.id ?? '', search: search.value || null, page: 1 }),
    () => ({ clientId: 'publicApi', enabled: account() !== null }),
  )

  const candidates = computed<OwnerOption[]>(() =>
    (rootOf(result.value)?.teamMembersAndOwners.data ?? []).map(toOwnerOption),
  )

  const loadingMore = ref(false)

  const loadMore = async () => {
    const paginator = rootOf(result.value)?.teamMembersAndOwners.paginatorInfo

    if (loadingMore.value || !paginator?.hasMorePages) {
      return
    }

    loadingMore.value = true

    try {
      await fetchMore({
        variables: { page: paginator.currentPage + 1 },
        updateQuery: (previous, { fetchMoreResult }) => {
          const key = previous.recruiter ? 'recruiter' : 'company'
          const before = previous[key]
          const after = fetchMoreResult?.[key]

          if (!before || !after) {
            return previous
          }

          return {
            [key]: {
              ...before,
              teamMembersAndOwners: {
                ...after.teamMembersAndOwners,
                data: [...before.teamMembersAndOwners.data, ...after.teamMembersAndOwners.data],
              },
            },
          }
        },
      })
    } catch {
      // The list keeps what it has; scrolling to the end again retries.
    } finally {
      loadingMore.value = false
    }
  }

  return { candidates, loading, loadMore }
}
